import { Component, OnDestroy, OnInit, ViewEncapsulation, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { LegacyShimsModule } from 'apps/account/src/account-shared/legacy-shims/legacy-shims.module';
import dayjs from 'dayjs/esm/index';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtSelectComponent } from '../../advocacy-group/touchpoint-sheets/ft-select.component';
import PropertyApiActions from '../../../../account-v2/src/app/actions/propertyApi';
import { ShareType } from '../../../../account-v2/src/app/components/broadcast/shareType';
import { StoryDescriptor } from '../../../../account-v2/src/app/components/broadcast/storyDescriptor';
import { BroadcastFlowBase, BroadcastFlowStep } from './broadcast-flow-base';
import { FtBroadcastChooseGoalComponent } from './ft-broadcast-choose-goal.component';
import { FtBroadcastChooseNetworkComponent } from './ft-broadcast-choose-network.component';
import { FtBroadcastShareImageReadyComponent } from './ft-broadcast-share-image-ready.component';
import { FtStoryImageSetupComponent } from './ft-story-image-setup.component';

/**
 * Build-broadcast-recommendation modal flow — walks the goal → image-setup →
 * channel → share steps, then concludes by creating a recommendation (message +
 * expiration). Standalone — mounted via createComponent.
 */
@Component({
    selector: 'ft-build-broadcast-recommendation-modal-ng',
    imports: [
        FormsModule,
        TranslateModule,
        LegacyShimsModule,
        FtModalComponent,
        FtModalChromeComponent,
        FtSelectComponent,
        FtBroadcastChooseGoalComponent,
        FtBroadcastChooseNetworkComponent,
        FtBroadcastShareImageReadyComponent,
        FtStoryImageSetupComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-build-broadcast-recommendation-modal.component.html',
})
export class FtBuildBroadcastRecommendationModalComponent extends BroadcastFlowBase implements OnInit, OnDestroy {
    static readonly ShareTypeToRecommendationTypeMap: Record<number, number> = {
        [ShareType.Story]: 1,
        [ShareType.PhotoContestInvitation]: 5,
    };

    protected readonly recommendationExpirationRanges = [
        { name: '7 days', date: dayjs().add(7, 'days') },
        { name: '14 days', date: dayjs().add(14, 'days') },
        { name: '30 days', date: dayjs().add(30, 'days') },
    ];

    protected recommendationExpirationDate = Object.assign({}, this.recommendationExpirationRanges[2]);
    protected readonly recommendationMessage = signal<any>(undefined);
    protected readonly isProcessingRequest = signal(false);
    protected readonly recommendationSubmitted = signal(false);

    ngOnInit(): void {
        this.steps = [
            new BroadcastFlowStep('ChooseGoal', true),
            new BroadcastFlowStep('StoryShareImageSetup'),
            new BroadcastFlowStep('PhotocontestInvitationShareImageSetup'),
            new BroadcastFlowStep('ChooseNetwork'),
            new BroadcastFlowStep('CreateImageProgress'),
            new BroadcastFlowStep('ShareImageReady'),
            new BroadcastFlowStep('ConcludeRecommendation'),
        ];

        document.body.classList.add('modal-open', 'modal-with-am-fade');

        // Verbatim BuildBroadcastStoryRecommendationFlowController ctor body.
        this.property = this.sessionService.getProperty(this.propertyUuid());
        this.fullContext = `/undefined/${this.spacesService.getPropertyUuid()}`;

        Promise.all([
            this.photoContest.details(this.propertyUuid(), this.storyUuid()),
            this.layersApi.photocontest({ propertyId: this.propertyUuid() }),
        ]).then((datas: any[]) => {
            this.zone.run(() => {
                const story = datas[0],
                    photoContestLayer = datas[1];

                this.storyDescriptor = new StoryDescriptor(this.parse, story);
                this.storySharedTo = story.socialNetworksSharing
                    .filter((n: any) => this.parse.intOrDefault(n.totalShareTimes) > 0)
                    .map((n: any) => this.parse.intOrDefault(n.socialNetworkType));
                this.contests = this.parse.arrayOrDefault(photoContestLayer.contests);
                this.imageSetupOptions = this.createImageSetupOptionsFromStory(story);

                const hasActiveContests =
                    !!this.contests &&
                    !!this.contests.find(
                        (c: any) => this.parse.bool(c.isActive) && !this.parse.bool(c.isClosed) && this.daysUntilDate(c.endDate) >= 1,
                    );
                if (!hasActiveContests) {
                    this.startLeadShare();
                }

                Promise.resolve(
                    !!story.guest.poststay.photo
                        ? this.photoContest.load({ contestId: story.guest.poststay.photo.photoContest.uuid, propertyUuid: this.propertyUuid() })
                        : null,
                ).then((contest: any) => {
                    this.zone.run(() => {
                        this.contest = contest;
                        this.story = story;
                        this.isInitialized = true;

                        this.setShareType(ShareType.Story);
                    });
                });
            });
        });
    }

    ngOnDestroy(): void {
        document.body.classList.remove('modal-open', 'modal-with-am-fade');
    }

    protected chooseDifferentGoal(): void {
        this.moveTo(BroadcastFlowBase.STEP_NAME_CHOOSE_GOAL);
    }

    protected startLeadShare(): void {
        this.setShareType(ShareType.Story);
        this.moveTo(BroadcastFlowBase.STEP_NAME_STORY_IMAGE_SETUP);
    }

    protected startStorytellersShare(contest: any): void {
        this.inviteToContest = contest;
        this.setShareType(ShareType.PhotoContestInvitation);
        this.moveTo(BroadcastFlowBase.STEP_NAME_PHOTOCONTEST_INVITATION_IMAGE_SETUP);
    }

    protected returnToImageSetup(): void {
        this.moveTo(
            this.shareType == ShareType.Story
                ? BroadcastFlowBase.STEP_NAME_STORY_IMAGE_SETUP
                : BroadcastFlowBase.STEP_NAME_PHOTOCONTEST_INVITATION_IMAGE_SETUP,
        );
    }

    protected finishImageSetup(): void {
        this.moveTo(BroadcastFlowBase.STEP_NAME_CHOOSE_NETWORK);
    }

    protected createRecommendation(recommendationForm: NgForm): void {
        // v1 set `recommendationForm.$submitted = true` manually (the button is
        // type=button); NgForm.submitted is read-only → a local mirror flag.
        this.recommendationSubmitted.set(true);
        if (recommendationForm.invalid) return;
        const shareImageUuid = !!this.shareImageUrl ? this.shareImageUrl.substr(this.shareImageUrl.length - 40, 36) : null;
        const customData: any = {
            imageSetupOptions: this.imageSetupOptions,
        };
        customData['guestUuid'] = this.story.guest.uuid;
        customData['selectedPhotoUrl'] = this.imageSetupOptions.isShowGuestPhoto
            ? this.ftAzureStoredPhoto(this.story.guest.poststay.photo)
            : this.property.BackgroundImageUrl;
        if (!!this.story.guest.poststay.photo) {
            customData['photoUuid'] = this.story.guest.poststay.photo.uuid;
        }
        if (this.network) {
            customData['socialNetworkType'] = this.network.type;
            customData['shareImageUrl'] = this.shareImageUrl;
        }
        if (this.inviteToContest) {
            customData['contestUuid'] = this.inviteToContest.id;
        }
        this.isProcessingRequest.set(true);
        this.dispatcher
            .dispatch(
                PropertyApiActions.createRecommendation(
                    this.property.UUID,
                    shareImageUuid,
                    this.recommendationExpirationDate.date,
                    this.recommendationMessage(),
                    FtBuildBroadcastRecommendationModalComponent.ShareTypeToRecommendationTypeMap[this.shareType!],
                    customData,
                ),
            )
            .then(
                () => {
                    this.zone.run(() => {
                        this.close();
                        // v1 then-broadcast '[Broadcast].CreateRecommendationComplete' — zero
                        // listeners anywhere (event census) → dropped.
                    });
                },
                () => {
                    this.zone.run(() => alert('Unable to create recommendation.'));
                },
            )
            .finally(() => {
                this.isProcessingRequest.set(false);
            });
    }

    protected finishRecommendation(): void {
        this.moveTo(BroadcastFlowBase.STEP_NAME_CONCLUDE_RECOMMENDATION);
    }
}
